import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, Card, Col, Input, Label, Row, Table } from 'reactstrap';

import TenderStatusBadge from './TenderStatusBadge';
import TenderPagination from './TenderPagination';
import {
  formatCount,
  formatDisplayDate,
  formatDisplayTime,
  formatText,
} from './formatters';
import {
  CATEGORY_TABS,
  PUBLIC_STATUS,
  fetchTenderAnnouncementSummary,
  fetchTenderAnnouncements,
} from './services/tenderAnnouncementService';
import { usePublicLanguage } from './i18n/LanguageContext';
import logoWhite from '../../assets/images/pertamina-white.svg';

const PAGE_SIZE = 10;

// Nilai awal filter: 1 Januari tahun berjalan s/d hari ini.
const buildInitialFilters = () => ({
  search: '',
  announcementDateFrom: dayjs().startOf('year').format('YYYY-MM-DD'),
  announcementDateTo: dayjs().format('YYYY-MM-DD'),
  status: '',
});

const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => i);

// Kolom tabel beserta kunci sort-nya. Kunci dikirim apa adanya ke API, yang
// hanya menerima nilai dari daftar sahnya sendiri.
//
// Tanggal dan jam ditampilkan dalam SATU kolom: keduanya berasal dari nilai yang
// sama, yaitu waktu pengumuman diterbitkan.
const SORTABLE_COLUMNS = [
  { key: 'announcementDate', label: 'table.announcementDate' },
  { key: 'title', label: 'table.title' },
  { key: 'category', label: 'table.category' },
  { key: 'deadline', label: 'table.deadline' },
  { key: 'status', label: 'table.status' },
  { key: 'bids', label: 'table.bid', align: 'end' },
];

// Jumlah kolom tabel (D-24) — dipakai colSpan baris kosong/gagal.
const COLUMN_COUNT = 6;

const PublicTenderAnnouncement = () => {
  const navigate = useNavigate();
  const { t, language } = usePublicLanguage();

  // `draft` = isi kotak filter, `applied` = filter yang sedang dipakai.
  // Dipisah supaya tabel hanya berubah saat tombol Cari ditekan.
  const [draft, setDraft] = useState(buildInitialFilters);
  const [applied, setApplied] = useState(buildInitialFilters);
  const [pageNumber, setPageNumber] = useState(1);
  // Tab kategori (D-23). Terpisah dari `draft`/`applied` karena berpindah tab
  // langsung memuat ulang data, tanpa menunggu tombol Cari.
  const [activeTab, setActiveTab] = useState(CATEGORY_TABS[0].key);
  // Pengurutan tabel. Dikirim ke API, bukan diproses di klien — tabelnya
  // dipaginasi, jadi mengurutkan sepuluh baris yang kebetulan tampil akan
  // menyesatkan ("Bid tertinggi" jadi tertinggi di halaman itu saja).
  const [sort, setSort] = useState({ by: 'announcementDate', dir: 'desc' });

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ activeCount: 0, finishedCount: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTenderAnnouncements({
        ...applied,
        status: applied.status || null,
        announcementDateFrom: applied.announcementDateFrom || null,
        announcementDateTo: applied.announcementDateTo || null,
        category:
          CATEGORY_TABS.find(tab => tab.key === activeTab)?.category ?? null,
        sortBy: sort.by,
        sortDir: sort.dir,
        pageNumber,
        pageSize: PAGE_SIZE,
        language,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(t('state.error'));
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [applied, activeTab, sort, pageNumber, language, t]);

  useEffect(() => {
    document.title = t('list.documentTitle');
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let active = true;
    fetchTenderAnnouncementSummary().then(result => {
      if (active) setSummary(result);
    });
    return () => {
      active = false;
    };
  }, []);

  const handleDraftChange = field => event => {
    setDraft(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSearch = event => {
    event.preventDefault();
    setPageNumber(1);
    setApplied(draft);
  };

  const handleReset = () => {
    const initial = buildInitialFilters();
    setDraft(initial);
    setApplied(initial);
    setPageNumber(1);
  };

  // Klik pertama pada kolom baru mulai dari menurun (nilai terbesar dulu),
  // klik berikutnya membalik arah. Mengurutkan selalu kembali ke halaman 1:
  // tetap di halaman 5 setelah urutan berubah hanya membingungkan.
  const handleSort = key => {
    setSort(prev =>
      prev.by === key
        ? { by: key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { by: key, dir: 'desc' }
    );
    setPageNumber(1);
  };

  const handleTabChange = key => {
    if (key === activeTab) return;
    setActiveTab(key);
    setPageNumber(1);
  };

  const openDetail = id => navigate(`/tender-announcement/${id}`);

  const renderTableBody = () => {
    if (loading) {
      return SKELETON_ROWS.map(key => (
        <tr key={`skeleton-${key}`} className='public-tender__skeleton-row'>
          {Array.from({ length: COLUMN_COUNT }, (_, cell) => (
            <td key={cell}>
              <span className='public-tender__skeleton' />
            </td>
          ))}
        </tr>
      ));
    }

    if (error) {
      return (
        <tr>
          <td colSpan={COLUMN_COUNT}>
            <div className='public-tender__empty text-center'>
              <i
                className='ri-error-warning-line public-tender__empty-icon'
                aria-hidden='true'
              ></i>
              <p className='mb-3'>{error}</p>
              <Button color='primary' outline onClick={loadData}>
                {t('state.retry')}
              </Button>
            </div>
          </td>
        </tr>
      );
    }

    if (items.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMN_COUNT}>
            <div className='public-tender__empty text-center'>
              <i
                className='ri-inbox-line public-tender__empty-icon'
                aria-hidden='true'
              ></i>
              <p className='mb-3'>{t('state.empty')}</p>
              <Button color='primary' outline onClick={handleReset}>
                {t('state.resetFilter')}
              </Button>
            </div>
          </td>
        </tr>
      );
    }

    return items.map(item => (
      <tr
        key={item.id}
        className='public-tender__row'
        onClick={() => openDetail(item.id)}
      >
        <td className='public-tender__cell-date'>
          <div>{formatDisplayDate(item.announcementDate, language)}</div>
          <div className='text-muted small text-nowrap'>
            {formatDisplayTime(item.announcementDate)}
          </div>
        </td>
        <td>
          <Link
            to={`/tender-announcement/${item.id}`}
            className='public-tender__title-link'
            onClick={event => event.stopPropagation()}
          >
            {item.titleProcurement}
          </Link>
          <div className='text-muted small mt-1'>
            {formatText(item.procurementNumber)}
          </div>
        </td>
        <td>
          {item.procurementCategory ? (
            <span className='badge bg-light text-body public-tender__category'>
              {item.procurementCategory}
            </span>
          ) : (
            <span className='text-muted'>{formatText(null)}</span>
          )}
        </td>
        <td className='public-tender__cell-date'>
          {formatDisplayDate(item.proposalSubmissionDeadline, language)}
          {item.proposalSubmissionDeadline && (
            <div className='text-muted small text-nowrap'>
              {formatDisplayTime(item.proposalSubmissionDeadline)}
            </div>
          )}
        </td>
        <td>
          <TenderStatusBadge status={item.publicStatus} />
        </td>
        {/* Kolom BID membaca `bids` — jumlah penawar, bukan jumlah halaman
            dibuka (D-35). Baris lama bernilai 0 dan memang ditampilkan 0
            (D-47), bukan tanda hubung. */}
        <td className='text-end text-muted small text-nowrap'>
          {formatCount(item.bids ?? 0, language)}
        </td>
      </tr>
    ));
  };

  return (
    <Row className='g-4 align-items-start'>
      {/* Satu kolom penuh. Panel login yang dulu menempel di kanan dihapus:
          rancangan gambar 3 memakai lebar penuh, dan pintu masuk login kini
          ada di landing `/` (D-26) sehingga form di sini jadi mubazir. */}
      <Col xs={12}>
        <Card className='public-tender__card border-0 overflow-hidden'>
          {/* Panel hero meniru `.login-hero` pada halaman login: foto kapal,
          lapisan gradasi, dan garis aksen — hanya diatur mendatar sebagai
          kepala kartu, bukan kolom setinggi penuh. */}
          <div className='public-tender__hero'>
            <div className='public-tender__hero-overlay' aria-hidden='true' />
            <div className='public-tender__hero-accent' aria-hidden='true' />

            <div className='public-tender__hero-content'>
              <div className='public-tender__brand'>
                <img src={logoWhite} alt='Pertamina' height='30' />
                <span className='public-tender__brand-text'>
                  {t('layout.brand')}
                </span>
              </div>

              <h1 className='public-tender__hero-title'>{t('list.title')}</h1>
              <p className='public-tender__hero-desc'>{t('list.desc')}</p>

              <div className='d-flex flex-wrap gap-2'>
                <span className='public-tender__chip'>
                  <i className='ri-flashlight-line me-1' aria-hidden='true'></i>
                  {t('list.chipActive', { count: summary.activeCount })}
                </span>
                <span className='public-tender__chip'>
                  <i
                    className='ri-check-double-line me-1'
                    aria-hidden='true'
                  ></i>
                  {t('list.chipFinished', { count: summary.finishedCount })}
                </span>
              </div>
            </div>
          </div>

          <div className='public-tender__panel'>
            <ul className='nav nav-tabs public-tender__tabs' role='tablist'>
              {CATEGORY_TABS.map(tab => (
                <li className='nav-item' key={tab.key} role='presentation'>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={activeTab === tab.key}
                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    {t(`tab.${tab.key}`)}
                  </button>
                </li>
              ))}
            </ul>

            <div className='public-tender__filter'>
              <form onSubmit={handleSearch}>
                <Row className='g-3 align-items-end'>
                  <Col lg={4} md={6}>
                    <Label htmlFor='tender-search' className='form-label'>
                      {t('filter.search')}
                    </Label>
                    <div className='position-relative'>
                      <Input
                        id='tender-search'
                        type='text'
                        placeholder={t('filter.searchPlaceholder')}
                        value={draft.search}
                        onChange={handleDraftChange('search')}
                        className='public-tender__search-input'
                      />
                      <i
                        className='ri-search-line public-tender__search-icon'
                        aria-hidden='true'
                      ></i>
                    </div>
                  </Col>

                  <Col lg={2} md={3} sm={6}>
                    <Label htmlFor='tender-date-from' className='form-label'>
                      {t('filter.dateFrom')}
                    </Label>
                    <Input
                      id='tender-date-from'
                      type='date'
                      value={draft.announcementDateFrom}
                      onChange={handleDraftChange('announcementDateFrom')}
                    />
                  </Col>

                  <Col lg={2} md={3} sm={6}>
                    <Label htmlFor='tender-date-to' className='form-label'>
                      {t('filter.dateTo')}
                    </Label>
                    <Input
                      id='tender-date-to'
                      type='date'
                      value={draft.announcementDateTo}
                      onChange={handleDraftChange('announcementDateTo')}
                    />
                  </Col>

                  <Col lg={2} md={6} sm={6}>
                    <Label htmlFor='tender-status' className='form-label'>
                      {t('filter.status')}
                    </Label>
                    <Input
                      id='tender-status'
                      type='select'
                      value={draft.status}
                      onChange={handleDraftChange('status')}
                    >
                      <option value=''>{t('filter.allStatus')}</option>
                      <option value={PUBLIC_STATUS.ANNOUNCED}>
                        {t('status.Announced')}
                      </option>
                      <option value={PUBLIC_STATUS.BIDDING_OPEN}>
                        {t('status.BiddingOpen')}
                      </option>
                      <option value={PUBLIC_STATUS.BIDDING_CLOSED}>
                        {t('status.BiddingClosed')}
                      </option>
                    </Input>
                  </Col>

                  <Col lg={2} md={6} sm={12}>
                    <div className='d-flex gap-2'>
                      <Button
                        color='primary'
                        type='submit'
                        className='flex-grow-1 public-tender__btn-primary'
                      >
                        <i
                          className='ri-search-line me-1'
                          aria-hidden='true'
                        ></i>
                        {t('filter.submit')}
                      </Button>
                      <Button color='light' type='button' onClick={handleReset}>
                        {t('filter.reset')}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </form>
            </div>

            <div className='table-responsive public-tender__table-wrap'>
              <Table className='public-tender__table align-middle mb-0'>
                <thead>
                  <tr>
                    {SORTABLE_COLUMNS.map(col => {
                      const isActive = sort.by === col.key;
                      const nextDir =
                        isActive && sort.dir === 'desc' ? 'asc' : 'desc';
                      return (
                        <th
                          key={col.key}
                          scope='col'
                          aria-sort={
                            isActive
                              ? sort.dir === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                          }
                          className={col.align === 'end' ? 'text-end' : ''}
                        >
                          <button
                            type='button'
                            className={`public-tender__sort ${
                              isActive ? 'is-active' : ''
                            }`}
                            onClick={() => handleSort(col.key)}
                            title={t(
                              nextDir === 'asc'
                                ? 'table.sortAsc'
                                : 'table.sortDesc'
                            )}
                          >
                            <span>{t(col.label)}</span>
                            {/* Ikon hanya pada kolom yang sedang diurutkan.
                                Menampilkannya di semua kolom membuat baris
                                header penuh panah dan justru menyulitkan
                                melihat kolom mana yang sedang aktif. */}
                            {isActive && (
                              <i
                                aria-hidden='true'
                                className={`public-tender__sort-icon ${
                                  sort.dir === 'asc'
                                    ? 'ri-arrow-up-s-fill'
                                    : 'ri-arrow-down-s-fill'
                                }`}
                              />
                            )}
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </Table>
            </div>

            {!loading && !error && items.length > 0 && (
              <TenderPagination
                totalCount={totalCount}
                pageNumber={pageNumber}
                pageSize={PAGE_SIZE}
                onPageChange={setPageNumber}
              />
            )}
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default PublicTenderAnnouncement;
